import React, { useState } from "react";

import { useKey } from "@/client/hooks/useKey";

// ─── Link ────────────────────────────────────────────────────────────────────

/** SPA-aware link: renders an <a> with href for accessibility, intercepts clicks for client-side navigation. */
export function Link({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    // Allow modifier keys for open-in-new-tab etc.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    window.history.pushState(null, "", href);
    window.dispatchEvent(new Event("locationchange"));
  }

  return (
    <a href={href} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────

const BADGE_COLORS: Record<string, string> = {
  main: "bg-term-accent/15 text-term-accent border-term-accent/30",
  side: "bg-term-user/15 text-term-user border-term-user/30",
  base: "bg-term-text-muted/15 text-term-text-dim border-term-border",
  dessert: "bg-term-dessert/15 text-term-dessert border-term-dessert/30",
  "meal-prep": "bg-term-accent/10 text-term-accent-dim border-term-accent/20",
  "day-of-eating": "bg-term-user/10 text-term-user border-term-user/20",
};

const BADGE_LABELS: Record<string, string> = {
  main: "Principal",
  side: "Guarnición",
  base: "Base",
  dessert: "Postre",
  "meal-prep": "Meal prep",
  "day-of-eating": "Día de",
};

export function Badge({ value, className }: { value: string; className?: string }) {
  const color = BADGE_COLORS[value] ?? "bg-term-raised text-term-text-dim border-term-border";
  const label = BADGE_LABELS[value] ?? value;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${color} ${className ?? ""}`}
    >
      {label}
    </span>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  onClick,
  index,
  hoverable,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  /** Pass an index for staggered fade-in animation */
  index?: number;
  /** Enable hover styles (shadow, border, lift) without requiring onClick */
  hoverable?: boolean;
}) {
  // Capture index on first render; keep it so re-renders don't remove the animation mid-flight.
  const [animIndex] = useState(() => index);
  const shouldAnimate = animIndex != null;
  const style = shouldAnimate
    ? ({ animationDelay: `${animIndex * 50}ms` } as React.CSSProperties)
    : undefined;
  return (
    <div
      className={`bg-term-surface border border-term-border rounded-xl shadow-sm transition-all ${
        onClick || hoverable
          ? "cursor-pointer hover:border-term-border-bright hover:shadow-md hover:-translate-y-0.5"
          : ""
      } ${shouldAnimate ? "animate-[fade-in-up_0.3s_ease_both]" : ""} ${className ?? ""}`}
      style={style}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// ─── PageHeader ───────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  icon,
  subtitle,
  actions,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="font-serif text-2xl text-term-text flex items-center gap-2.5">
          {icon && <span className="text-term-accent-dim">{icon}</span>}
          {title}
        </h1>
        {subtitle && (
          <p className={`text-sm text-term-text-dim mt-0.5 ${icon ? "ml-[32.5px]" : ""}`}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// ─── Button ───────────────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-term-accent text-white hover:bg-term-accent/85 shadow-sm hover:shadow-md",
  secondary:
    "bg-term-raised border border-term-border text-term-text hover:border-term-border-bright",
  ghost: "text-term-text-dim hover:text-term-text hover:bg-term-raised",
  danger: "bg-term-error/10 text-term-error border border-term-error/30 hover:bg-term-error/20",
};

export function Button({
  children,
  variant = "secondary",
  size = "md",
  onClick,
  disabled,
  type = "button",
  className,
}: {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  className?: string;
}) {
  const sizeClass =
    size === "sm"
      ? "px-2.5 py-1.5 text-sm"
      : size === "lg"
        ? "px-5 py-2.5 text-sm"
        : "px-3.5 py-2 text-sm";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-lg font-medium cursor-pointer transition-all disabled:opacity-40 disabled:cursor-not-allowed ${sizeClass} ${BUTTON_VARIANTS[variant]} ${className ?? ""}`}
    >
      {children}
    </button>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <div className="flex min-h-[3.5rem] items-center justify-center text-term-accent [&>svg]:h-12 [&>svg]:w-12">
        {icon}
      </div>
      <h3 className="font-serif text-lg text-term-text">{title}</h3>
      {description && <p className="text-sm text-term-text-muted max-w-xs">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ className }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center py-12 ${className ?? ""}`}>
      <div className="w-6 h-6 border-2 border-term-border border-t-term-accent rounded-full animate-spin" />
    </div>
  );
}

// ─── TopLoadingBar ───────────────────────────────────────────────────────────

/** Thin animated bar at the top of the viewport, shown when `visible` is true. */
export function TopLoadingBar({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="fixed top-0 left-0 right-0 h-0.5 z-[100] overflow-hidden bg-term-accent/20">
      <div className="h-full w-1/3 bg-term-accent rounded-full animate-[loading-slide_1s_ease-in-out_infinite]" />
    </div>
  );
}

// ─── ErrorMessage ─────────────────────────────────────────────────────────────

export function ErrorMessage({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <p className="text-term-error text-sm">{message}</p>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function Modal({
  title,
  children,
  onClose,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
}) {
  useKey("Escape", onClose);

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 animate-[fade-in_0.15s_ease]"
        onClick={onClose}
      />
      <div className="relative bg-term-surface border border-term-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-term-border">
          <h2 className="font-serif text-lg text-term-text">{title}</h2>
          <button
            onClick={onClose}
            className="text-term-text-muted hover:text-term-text cursor-pointer w-7 h-7 flex items-center justify-center rounded-full hover:bg-term-raised transition-all text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-term-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FormField ────────────────────────────────────────────────────────────────

export function FormField({
  label,
  children,
  hint,
  error,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-term-text-dim uppercase tracking-wide">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-term-text-muted">{hint}</p>}
      {error && <p className="text-xs text-term-error">{error}</p>}
    </div>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────────

export function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
  className,
  autoFocus,
  inputRef,
  onKeyDown,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}) {
  return (
    <input
      ref={inputRef}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      className={`w-full px-3 py-2 text-sm bg-term-bg border border-term-border rounded-lg text-term-text placeholder:text-term-text-muted focus:border-term-accent-dim focus:outline-none disabled:opacity-50 transition-colors ${className ?? ""}`}
    />
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 pr-8 text-sm bg-term-bg border border-term-border rounded-lg text-term-text focus:border-term-accent-dim focus:outline-none disabled:opacity-50 transition-colors appearance-none bg-[length:16px_16px] bg-[position:right_8px_center] bg-no-repeat"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23a08b7a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────

export function ConfirmModal({
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  danger = false,
  onConfirm,
  onCancel,
}: {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useKey("Escape", onCancel);

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-term-surface border border-term-border rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-5">
        <p className="text-sm text-term-text leading-relaxed">{message}</p>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
